import { get as _get } from 'lodash';
import React, { useState, useEffect } from 'react';
import { Constants } from '@pwc/pwc-deals-web-common';
import { MiniLoader } from '@pwc/pwc-deals-ui';
import UnableToLoadContent from 'features/analyses/common/UnableToLoadContent/UnableToLoadContent';
import { hasLibraryItemError } from 'features/analyses/common/utils';
import ObjectCommentWrapper from 'common/components/ObjectCommentWrapper';
import ExcelPPTLibraryItemCard from 'features/analyses/common/LibraryItemCard/ExcelPPTLibraryItemCard';
import { isPowerPivotLibraryItem } from 'features/analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes.ts';
import PowerPivotLibraryItemCard from 'features/analyses/common/LibraryItemCard/PowerPivot/components/PowerPivotLibraryItemCard.tsx';
import PowerBiReport from '../../../features/analyses/PowerBiReport/PowerBiReport';
import RichTextLibraryItem from '../../charts/types/ContentItem/RichTextLibraryItem';
import { TABLEAU_LIBRARY_ITEM_TYPES } from '../SectionArea/libraryItemUtils.ts';
import TableauReport from '../../../features/analyses/TableauReport/TableauReport';

const AnalysisLibraryItem = React.memo(
  ({ item, projectId, isInMultipleColumnLayout }) => {
    const [tableauTab, setTableauTab] = useState(undefined);
    const renderableContentItemTypes = [
      Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value,
      Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value,
      Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value,
      Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value,
    ];

    const isPowerBiReport =
      item.type === Constants.LIBRARY_ITEM_TYPES.POWER_BI_REPORT.value ||
      item.type === Constants.LIBRARY_ITEM_TYPES.POWER_BI_REPORT_PAGE.value;

    const isTableauReport =
      item.type === TABLEAU_LIBRARY_ITEM_TYPES.TableauReport ||
      item.type === TABLEAU_LIBRARY_ITEM_TYPES.TableauReportPage;

    const isPowerPivot = isPowerPivotLibraryItem(item.type);

    useEffect(() => {
      const parseData = () => {
        if (item && item.data) {
          try {
            const parsedData = JSON.parse(item.data);
            return parsedData.TabName || parsedData.tabName;
          } catch (error) {
            return undefined;
          }
        }
        if (item && item.content) {
          try {
            const parsedData = JSON.parse(item.content);
            return parsedData.TabName || parsedData.tabName;
          } catch (error) {
            return undefined;
          }
        }
        return undefined;
      };

      const getTabName = () =>
        item.tabName ||
        _get(item, ['data', 'tabName'], undefined) ||
        parseData();

      if (isTableauReport) {
        setTableauTab(getTabName());
      } else {
        setTableauTab(undefined);
      }
    }, [item, isTableauReport]);

    if (isPowerPivot && item.isShared && item.content) {
      try {
        item.content = JSON.parse(item.content);
        item.data = {
          name: item.content.Name,
          embeddedUrl: item.content.EmbeddedUrl,
          worksheetName: item.content.WorksheetName,
          sharePointItemUrl: item.content.SharePointItemUrl,
          errorCode: item.content.ErrorCode,
        };
        if (item.status === 2 && item.content.HideWorksheets === true) {
          item.data.hideWorkSheets = true;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          `Error while parsing power pivot library item data: ${error}`
        );
      }
    }

    if (item.isLoading) {
      return (
        <div data-testid="mini-loader-container">
          <MiniLoader />
        </div>
      );
    }

    if (hasLibraryItemError(item)) {
      return (
        <UnableToLoadContent
          type={
            (typeof item.type === 'number' && item.type) ||
            (item.autoSync && item.autoSync.type)
          }
          data={{
            sharePointItemUrl: item.sharePointItemUrl || item.fileName,
            worksheetName: item.autoSync && item.autoSync.data.worksheetName,
            chartName: item.autoSync && item.autoSync.data.chartName,
            name: item.autoSync && item.autoSync.data.cellRange,
            slideNumber: item.slideNumber,
          }}
          projectId={projectId}
          errorCode={item?.error?.errorCode || item?.error?.apiErrorCode}
        />
      );
    }

    return (
      <div
        data-testid="analysis-library-item"
        data-library-item-name={item.name}
        className={`analysis-library-item-container2 css-override ${
          isInMultipleColumnLayout ? 'multiple-column-layout' : ''
        }`}
      >
        <div className="analysis-library-item-content2">
          {item.type === Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value && (
            <RichTextLibraryItem item={item} />
          )}
          {item.type !== Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value && (
            <ObjectCommentWrapper libraryItemId={item.id}>
              {renderableContentItemTypes.includes(item.type) && (
                <ExcelPPTLibraryItemCard
                  libraryItem={item}
                  projectId={projectId}
                  setIsDragDisabled={() => {}}
                />
              )}
              {isPowerBiReport && (
                <PowerBiReport
                  item={{
                    ...item,
                    pageName:
                      item.pageName ||
                      _get(item, ['data', 'pageName'], undefined),
                  }}
                  projectId={projectId}
                />
              )}

              {isTableauReport && (
                <TableauReport
                  item={{
                    ...item,
                    tabName: tableauTab,
                  }}
                  projectId={projectId}
                />
              )}
              {isPowerPivot && (
                <PowerPivotLibraryItemCard
                  libraryItem={item}
                  projectId={projectId}
                />
              )}
            </ObjectCommentWrapper>
          )}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) =>
    nextProps.item.id === prevProps.item.id &&
    nextProps.item.isShared === prevProps.item.isShared &&
    nextProps.item.hasError === prevProps.item.hasError
);
export default AnalysisLibraryItem;
